// How a score was reached (meeting 2026-10-26): "show me a table: this is how it got the 536". One site: its
// severity and analysis points, and crash points once crash data is loaded. A hotspot or project prospect: every
// site inside it, its points and the share of it that lies inside. Same figures the rank uses; also as a CSV.
import React from 'react';
import { Modal } from '../Overview/OverviewDialogs';
import { siteScoreParts, stretchBreakdown } from '../Safety/offenders';

const roadOf = (s) => (s.segment ? `${s.road} · ${s.segment}` : s.road);
const fmtMp = (s) => (s.mpEnd != null && s.mpEnd !== s.mpStart ? `MP ${s.mpStart.toFixed(2)}–${s.mpEnd.toFixed(2)}` : `MP ${(s.mpStart ?? 0).toFixed(2)}`);
const dirsOf = (s) => (s.directions || [s.direction]).join(' ');
const num = (v) => (Math.round(v * 10) / 10).toLocaleString('en-US');
const HEAD_HINTS = { 'In project limits': "How much of this site lies inside the project limits; only that part counts toward the project's score." };

/** The rows and columns shown, whichever kind of score it is. */
export function scoreTable(item, weights, crash, rowFlags = null) {
  // the right of way column: how much of a sight-distance site's sight line lies inside it (the others lie on the road)
  const withRow = Boolean(rowFlags && Object.keys(rowFlags).length);
  const rowOf = (s) => (rowFlags[s.id] ? `${rowFlags[s.id].pct}%` : '–');
  if (item.parts) {
    const { rows, total } = stretchBreakdown(item, weights);
    return {
      total,
      head: ['Analysis', 'Site', 'Dir', 'Severity', 'Severity weight', 'Analysis weight', 'In project limits', ...(withRow ? ['Right of way'] : []), 'Points'],
      rows: rows.map((r) => [r.site.analysis, fmtMp(r.site), dirsOf(r.site), r.site.severity, r.severity, r.analysis, `${Math.round(r.share * 100)}%`,
        ...(withRow ? [rowOf(r.site)] : []), num(r.points)]),
    };
  }
  const p = siteScoreParts(item, crash || {}, weights);
  const crashPts = p.crashCost + p.correlated;
  return {
    total: p.total,
    head: ['Analysis', 'Site', 'Dir', 'Severity', 'Severity weight', 'Analysis weight', ...(crashPts ? ['Crash weight'] : []), ...(withRow ? ['Right of way'] : []), 'Points'],
    rows: [[item.analysis, fmtMp(item), dirsOf(item), item.severity, p.severity, p.analysis, ...(crashPts ? [num(crashPts)] : []), ...(withRow ? [rowOf(item)] : []), num(p.total)]],
  };
}

function downloadCsv(name, table) {
  const cell = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [table.head, ...table.rows, [...table.head.slice(1).map(() => ''), num(table.total)]].map((r) => r.map(cell).join(','));
  const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ScoreDialog({ item, weights, crash = null, logic, onClose, rowFlags = null }) {
  const table = scoreTable(item, weights, crash, rowFlags);
  const title = item.parts ? `${item.label} · ${roadOf(item)} · ${fmtMp(item)}` : `${item.analysis} · ${roadOf(item)} · ${fmtMp(item)}`;
  return (
    <Modal title={`How the score of ${num(table.total)} was reached`} onClose={onClose}>
      <p className="safety-muted safety-modal-lede"><b>{title}</b></p>
      <p className="safety-muted">{logic}</p>
      <div className="so-score-wrap">
        <table className="so-score-table">
          <thead><tr>{table.head.map((h) => <th key={h} title={HEAD_HINTS[h]}>{h}</th>)}</tr></thead>
          <tbody>
            {table.rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>{r.map((v, k) => <td key={table.head[k]}>{v}</td>)}</tr>
            ))}
            <tr className="so-score-total"><td colSpan={table.head.length - 1}>Score</td><td>{num(table.total)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="safety-modal-foot">
        <span className="safety-muted">The rank orders by this score, worst first.</span>
        <div className="row-actions">
          <button type="button" className="safety-btn safety-btn-ghost" onClick={() => downloadCsv('score.csv', table)}>Download as CSV</button>
          <button type="button" className="safety-btn ov-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}
